import { fetchToken } from '@agissc/ktag-map';
import { useQuery } from '@tanstack/react-query';
import { Geometry } from 'geojson';
import { Coordinate } from 'ol/coordinate';
import {
  DefaultResultProperties,
  IdentifyResult,
  LayerIdentifyResponse,
  LayerResult,
} from './contract';
import config from '../../../utils/AppConfig';

export interface IdentifyMapserviceProps {
  topicLayerId: string;
  isPublic: boolean;
  layerType: string;
  visibleSublayers: number[];
}

const fetchIdentifyLocationlinfo = async (x: number, y: number, tolerance: number) => {
  const url = `${config.apis.identifyLocation}?x=${x}&y=${y}&tolerance=${tolerance}`;
  const requestApi = new Request(url);
  const response = await fetch(requestApi);
  const result = (await response.json()) as any;
  const label = `${result.coordinates.x.toFixed(2)}\n ${result.coordinates.y.toFixed(2)}`;

  return {
    properties: {
      label,
      address: result.address?.displayValue ?? '',
      municipality: result.municipality?.displayValue,
      parcelEgrid: result.parcel.eGrid,
      parcelNumber: result.parcel.number,
      parcelArea: result.parcel.area,
      parcel: result.parcel.displayValue,
      parcelAreaUnit: result.parcel.unit,
      elevation: result.height.displayValue,
      agobisLink: result.agobis.url,
      isCoordinateWithinCanton: result.isCoordinateWithinCanton,
    } as DefaultResultProperties,
    geometry: {
      type: 'Point',
      coordinates: [result.coordinates.x, result.coordinates.y],
    } as Geometry,
    identifyPoint: { x: result.coordinates.x, y: result.coordinates.y },
    id: `info_api_${result.coordinates.x}_${result.coordinates.y}_${Date.now()}`,
    type: 'Feature',
    label,
    results: [],
  };
};

const fetchIdentifyMapservice = async (
  x: number,
  y: number,
  tolerance: number,
  topicLayerName: string,
  isPublic: boolean,
  layers: number[] = [],
) => {
  const headers = new Headers();

  if (!isPublic) {
    const token = await fetchToken();
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(
    `${config.apis.identifyLayer}/${topicLayerName}/identify?x=${x}&y=${y}&tolerance=${tolerance}&layers=${layers}`,
    {
      method: 'GET',
      headers,
    },
  );

  const layerResults = (await response.json()) as LayerIdentifyResponse;
  layerResults.results = layerResults.results.map((layerResult: LayerResult) => {
    return { ...layerResult, topiclayerId: topicLayerName };
  });
  return layerResults as LayerIdentifyResponse;
};

export const useIdentifyLocationlInfo = (location: Coordinate, tolerance: number) => {
  return useQuery<IdentifyResult>({
    queryKey: ['identifyResult', location[0], location[1], tolerance],
    queryFn: () => fetchIdentifyLocationlinfo(location[0], location[1], tolerance),
    gcTime: 3600000,
    staleTime: Infinity,
  });
};

export const useIdentifyLayerInfo = (
  location: Coordinate,
  tolerance: number,
  identifyMapserviceProps: IdentifyMapserviceProps,
) => {
  return useQuery<LayerIdentifyResponse>({
    queryKey: [
      'layerResults',
      location[0],
      location[1],
      tolerance,
      identifyMapserviceProps.topicLayerId,
      identifyMapserviceProps.isPublic,
      identifyMapserviceProps.layerType,
      JSON.stringify(identifyMapserviceProps.visibleSublayers),
    ],
    queryFn: () =>
      fetchIdentifyMapservice(
        location[0],
        location[1],
        tolerance,
        identifyMapserviceProps.topicLayerId,
        identifyMapserviceProps.isPublic,
        identifyMapserviceProps.visibleSublayers,
      ),
    gcTime: 3600000,
    staleTime: Infinity,
    enabled:
      identifyMapserviceProps.layerType !== 'esriMapServer' ||
      identifyMapserviceProps.visibleSublayers.length > 0,
  });
};

export default useIdentifyLayerInfo;
